import React, { Suspense, lazy, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toggleBottomBar } from "sdk/actions";
import { PrivateRoute } from "./PrivateRoute";
import { PublicRoute } from "./PublicRoute";
import ScrollToTop from "react-router-scroll-top";

// Eager — app shell, auth screens, and 404 are needed immediately and are
// small; keeping them in the main bundle avoids an extra chunk fetch on login.
import Login from "../Auth/Login";
import ForgotPassword from "../Auth/ForgotPassword";
import ChangePassword from "../Auth/ChangePassword";
import VerifyOTP from "../Auth/VerifyOTP";
import MainContent from "../MainContent/MainContent";
import NotFoundPage from "../common/Errors/NotFoundPage";

// Lazy — authenticated pages are code-split, so a logged-out user hitting
// /login no longer downloads the whole app (Quiz, TopicMaterial, Reports, …).
const NewDashboard = lazy(() => import("../../pages/NewDashboard/new-dashboard"));
const MyCourses = lazy(() => import("../../pages/MyCourses"));
const CoursePackage = lazy(() => import("../../pages/CoursePackage"));
const CoursePackageDetail = lazy(() => import("../../pages/CoursePackageDetail"));
const Courses = lazy(() => import("../CoursesList/Courses"));
const CertificateList = lazy(() => import("../CoursesList/CertificateList"));
const Reports = lazy(() => import("../../pages/Reports/reports"));
const Survey = lazy(() => import("../../pages/Survey/survey"));
const SupportTicket = lazy(() => import("../../pages/Support/SupportTicket"));
const Support = lazy(() => import("../../pages/Support/support"));
const NotificationDetail = lazy(() => import("../common/Header/NotificationDetail"));
const TopicMaterial = lazy(() => import("../../pages/TopicMaterial"));
const Quiz = lazy(() => import("../../pages/quiz"));
const Settings = lazy(() => import("../../pages/Settings"));
const LeaderBoard = lazy(() => import("../Tabs/LeaderBoard"));
const LanguageSwitcher = lazy(() =>
  import("../../pages/languageSwitcher").then((m) => ({ default: m.LanguageSwitcher }))
);

export function Routes() {
  const location = useLocation();
  const dispatch = useDispatch();
  const authRoutes = [
    { path: '/login', component: Login, title: "Login | LMS" },
    { path: '/forgot-password', component: ForgotPassword, title: "Forgot Password | LMS" },
    { path: '/verify-otp', component: VerifyOTP, title: "Verify OTP | LMS" },
    { path: '/change-password', component: ChangePassword, title: "Change Password | LMS" },
  ];

  const appRoutes = [
    { path: '/dashboard', component: NewDashboard },
    { path: '/my-courses', component: MyCourses },
    { path: '/course-package', component: CoursePackage },
    { path: '/course-package/:id', component: CoursePackageDetail },
    { path: '/courses', component: Courses },
    { path: '/certificate-list', component: CertificateList },
    { path: '/reports/:slug/:id', component: Reports },
    { path: '/surveys', component: Survey },
    { path: '/support-ticket', component: SupportTicket },
    { path: '/support', component: Support },
    { path: '/notifications', component: NotificationDetail },
    { path: '/course/leaderboard/:id', component: LeaderBoard },
    { path: '/course/:id', component: TopicMaterial },
    { path: '/my-profile', component: Settings },
    { path: '/quiz/:mode/:slug/:id', component: Quiz },
    {path: '/change-language', component: LanguageSwitcher},
  ];

  useEffect(() => {
    dispatch(toggleBottomBar(false));
  }, [location?.pathname, dispatch]);

  return (
      <Switch>
        {/* Auth Routes */}
        {authRoutes.map((ele) => (
          <PublicRoute
            exact
            key={`auth-${ele.path}`}
            path={ele.path}
            component={ele.component}
            title={ele.title}
          />
        ))}

        {/* App Routes */}
        <Route
          path="/"
          render={() => (
            <MainContent>
              <Suspense fallback={<div>Loading...</div>}>
                <ScrollToTop>
                  <Switch>
                    {appRoutes.map((ele) => (
                      <PrivateRoute
                        exact
                        key={`app-${ele.path}`}
                        path={ele.path}
                        component={ele.component}
                      />
                    ))}
                    <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
                    <Route component={NotFoundPage} />
                  </Switch>
                </ScrollToTop>
              </Suspense>
            </MainContent>
          )}
        />

        {/* Default Route Redirection */}
        <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
  );
}
